<script setup lang="ts">
import type { FragmentType } from '@/gql';
import { graphql } from '@/gql';
import ButtonFlat from 'theme/components/atoms/ButtonFlat.vue';
import type { RadioBlockOption } from 'theme/components/molecules/RadioBlock.vue';
import RadioBlock from 'theme/components/molecules/RadioBlock.vue';
import SettingsDetailPageContainer from '~/components/organisms/SettingsDetailPageContainer/SettingsDetailPageContainer.vue';
import { captureException } from '~/utils/sentry-workaround';

const props = defineProps<{
  viewer: FragmentType<typeof SettingsNotificationTemplateViewerFragment>;
}>();

const SettingsNotificationTemplateViewerFragment = graphql(`
  fragment SettingsNotificationTemplateViewer on Viewer {
    id
    isSubscribedNotifications
  }
`);

const updateNotificationSubscription = graphql(`
  mutation updateNotificationSubscription($isSubscribed: Boolean!) {
    updateNotificationSubscription(isSubscribed: $isSubscribed) {
      id
      isSubscribedNotifications
    }
  }
`);

const viewerObj = useComputedFragment(
  SettingsNotificationTemplateViewerFragment,
  () => props.viewer
);
const selected = ref(viewerObj.value.isSubscribedNotifications);

const isChanged = computed(() => {
  return viewerObj.value.isSubscribedNotifications !== selected.value;
});

const options: RadioBlockOption<boolean, string>[] = [
  {
    value: true,
    content: '通知を受け取る',
  },
  {
    value: false,
    content: '通知を受け取らない',
  },
];

const { mutate, loading, onError, onDone } = useMutation(
  updateNotificationSubscription
);

const { error: alertError, success: alertSuccess } = useAlert();

onError((error) => {
  alertError('保存に失敗しました');
  captureException(error);
});

onDone(() => {
  alertSuccess('設定を保存しました');
});

const save = async () => {
  if (loading.value) {
    return;
  }

  await mutate({
    isSubscribed: selected.value,
  });
};
</script>

<template>
  <SettingsDetailPageContainer title="LINE・メール通知設定" class="container">
    <section class="template">
      <h3>アドバイザーからのコメント</h3>
      <p class="template__description">
        診断結果に対するアドバイザーからのコメントについての通知を受け取るかどうかを設定できます。
      </p>
      <div class="template__selections selection-wrapper">
        <RadioBlock
          v-for="opt in options"
          :key="opt.value.toString()"
          v-model="selected"
          :option="opt"
          class="selection"
        >
          <template #default>{{ opt.content }}</template>
        </RadioBlock>
      </div>
    </section>
    <div class="container__button-wrapper">
      <ButtonFlat
        variant="primary"
        :disabled="loading || !isChanged"
        @click="save"
      >
        設定を保存する
      </ButtonFlat>
    </div>
  </SettingsDetailPageContainer>
</template>

<style lang="scss" scoped>
.container {
  &__button-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 64px;
  }
}

.template {
  padding: 0 16px;

  &__description {
    margin-top: 4px;
  }

  &__selections {
    margin-top: 8px;
  }
}

.selection-wrapper {
  display: flex;
  gap: 16px;
}

.selection {
  flex: 1;
}

.loading {
  display: flex;
  justify-content: center;
  margin-top: 64px;
}
</style>
